// ============================================
// B1（2026-10-03）：3D 编辑器弹窗状态（温馨提示 / 套餐订阅）
// - noticeDismissed：点「不再提示」仅本次有效（刷新/重进编辑器恢复，按需求 Q1）
// - lastNoticeAt/lastUpgradeAt：频率节流（frequencyHours，0=每次进入都弹）
// ============================================
import { create } from 'zustand';
import { DEFAULT_POPUP_CONFIG, fetchPopupConfig, type Popup3dConfig } from '../api/marketingPopup';

interface PopupState {
  config: Popup3dConfig;
  loaded: boolean;
  noticeOpen: boolean;
  upgradeOpen: boolean;
  /** 「不再提示」仅本次：内存态（刷新重置） */
  noticeDismissed: boolean;
  lastNoticeAt: number | null;
  lastUpgradeAt: number | null;
  loadConfig: () => Promise<void>;
  openNotice: () => void;
  closeNotice: () => void;
  dismissNotice: () => void;
  openUpgrade: () => void;
  closeUpgrade: () => void;
  /** 测试用重置 */
  reset: () => void;
}

/** 频率是否满足（hours<=0 视为不限频=每次） */
export function popupFrequencyOk(lastAt: number | null, hours?: number): boolean {
  if (!hours || hours <= 0) return true;
  if (!lastAt) return true;
  return Date.now() - lastAt >= hours * 3600_000;
}

// 频率时间戳跨刷新持久化（刷新后仍按 frequencyHours 节流；noticeDismissed「仅本次」不持久化）
const LS_NOTICE = 'dtwin_popup:lastNoticeAt';
const LS_UPGRADE = 'dtwin_popup:lastUpgradeAt';
function readTs(key: string): number | null {
  if (typeof window === 'undefined') return null;
  const v = Number(window.localStorage.getItem(key));
  return Number.isFinite(v) && v > 0 ? v : null;
}
function writeTs(key: string, v: number): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(key, String(v)); } catch { /* 隐私模式忽略 */ }
}

export const usePopupStore = create<PopupState>((set, get) => ({
  config: DEFAULT_POPUP_CONFIG,
  loaded: false,
  noticeOpen: false,
  upgradeOpen: false,
  noticeDismissed: false,
  lastNoticeAt: readTs(LS_NOTICE),
  lastUpgradeAt: readTs(LS_UPGRADE),

  loadConfig: async () => {
    if (get().loaded) return;
    const config = await fetchPopupConfig();
    set({ config, loaded: true });
  },

  openNotice: () => {
    const now = Date.now();
    writeTs(LS_NOTICE, now);
    set({ noticeOpen: true, noticeDismissed: false, lastNoticeAt: now });
  },
  closeNotice: () => set({ noticeOpen: false }),
  dismissNotice: () => {
    const now = Date.now();
    writeTs(LS_NOTICE, now);
    writeTs(LS_UPGRADE, now);
    set({ noticeOpen: false, noticeDismissed: true, lastNoticeAt: now, upgradeOpen: true, lastUpgradeAt: now });
  },
  openUpgrade: () => {
    const now = Date.now();
    writeTs(LS_UPGRADE, now);
    set({ upgradeOpen: true, lastUpgradeAt: now });
  },
  closeUpgrade: () => set({ upgradeOpen: false }),

  reset: () =>
    set({
      config: DEFAULT_POPUP_CONFIG,
      loaded: false,
      noticeOpen: false,
      upgradeOpen: false,
      noticeDismissed: false,
      lastNoticeAt: null,
      lastUpgradeAt: null,
    }),
}));

// 调试入口（e2e/排查用，与 __deviceDataStore 同约定）
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__popupStore = usePopupStore;
}
